import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import {
  NotificationFrequencyResponse,
  NotificationFrequencyType,
} from "../../recoil/types";

import {
  DisableSection,
  ErrorWrapper,
  GeneralInfoText,
  GeneralInfoWrapper,
  ProfileEmailNotificationExplainer,
  ProfileEmailNotificationsButtonWrapper,
  ProfileEmailNotificationsGrid,
  ProfileEmailNotificationsGridLine,
  ProfileEmailNotificationsGridRows,
  ProfileEmailNotificationsHeader,
  ProfileEmailNotificationsOption,
  ProfileEmailNotificationsOptionGrid,
  ProfileEmailNotificationsSpinner,
} from "./Profile.styles";
import { Select } from "../shared/Select/Select";
import type { SelectOptions } from "../shared/Select/Select";
import { Button } from "../shared/Button/Button";
import { PostEmailNotifications } from "./Services/UpdateProfileInfoService";
import { providerEmailNotificationFrequency } from "../../recoil/atoms";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import loading from "../../assets/icons/loading.svg";

const REPORT_NOTIFICATION_OPTIONS: SelectOptions[] = [
  { value: "Immediately", label: "Immediately" },
  { value: "Daily", label: "Daily" },
  { value: "EveryTwoDays", label: "Every two days" },
  { value: "Weekly", label: "Weekly" },
  { value: "None", label: "None" },
];

const IMMEDIATE_DAILY_NONE_OPTIONS: SelectOptions[] = [
  { value: "Immediately", label: "Immediately" },
  { value: "Daily", label: "Daily" },
  { value: "None", label: "None" },
];

const MISSING_CLINICAL_INFO_OPTIONS: SelectOptions[] = [
  { value: "Immediately", label: "Immediately" },
  { value: "Daily", label: "Daily" },
  { value: "EveryTwoDays", label: "Every two days" },
  { value: "None", label: "None" },
];

const EXISTING_OPEN_ITEM_OPTIONS: SelectOptions[] = [
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "EveryTwoWeeks", label: "Bimonthly" },
  { value: "None", label: "None" },
];

const SUBSCRIPTION_OPTIONS: SelectOptions[] = [
  { value: "True", label: "Subscribed" },
  { value: "False", label: "Unsubscribed all" },
];

type NotificationFrequencyField = Exclude<
  keyof NotificationFrequencyResponse,
  "is_subscribed"
>;

type ExplainerType = "report" | "clinical" | "billing" | "treatment";

const getExplainerMessage = (
  frequency: NotificationFrequencyType,
  notificationType: ExplainerType,
) => {
  if (frequency === "None") {
    if (notificationType === "clinical") {
      return "Complete clinical information is required to avoid reporting delays.";
    }
    if (notificationType === "billing") {
      return "Complete billing information is required to file claims for patients.";
    }
    if (notificationType === "treatment") {
      return "Treatment notes can help prevent claim denials.";
    }
  }
  if (frequency === "Daily") return "12 pm Eastern Time everyday";
  if (frequency === "Weekly") return "12 pm Eastern Time on Monday";
  if (frequency === "EveryTwoWeeks")
    return "12 pm Eastern Time on the 1st and 15th every month";
  return "";
};

const EmailNotifications: React.FC<{
  notificationFrequency: NotificationFrequencyResponse;
  providerCfcEnabled: boolean;
}> = ({ notificationFrequency, providerCfcEnabled }) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [updatedFrequency, setUpdateFrequency] = useState(
    notificationFrequency,
  );
  const [, setNotificationFrequency] = useRecoilState(
    providerEmailNotificationFrequency,
  );
  const [notificationUpdated, setNotificationUpdated] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const hasNotificationChanges =
    JSON.stringify(notificationFrequency) !== JSON.stringify(updatedFrequency);

  const updateFrequency = (
    field: NotificationFrequencyField,
    value: string,
  ) => {
    setUpdateFrequency((previousFrequency) => ({
      ...previousFrequency,
      [field]: value as NotificationFrequencyType,
    }));
  };

  const renderFrequencyRow = ({
    field,
    title,
    description,
    options,
    explainerType,
    indent,
    inputProps,
  }: {
    field: NotificationFrequencyField;
    title: string;
    description?: string;
    options: SelectOptions[];
    explainerType: ExplainerType;
    indent?: boolean;
    inputProps?: React.ComponentProps<typeof Select>["inputProps"];
  }) => {
    const selectedFrequency = updatedFrequency[field];

    return (
      <ProfileEmailNotificationsGridRows
        none={selectedFrequency === "None"}
        key={field}
      >
        {indent ? (
          <ProfileEmailNotificationsOption indent>
            {title}
          </ProfileEmailNotificationsOption>
        ) : (
          <ProfileEmailNotificationsOptionGrid>
            <ProfileEmailNotificationsOption>
              {title}
            </ProfileEmailNotificationsOption>
            <ProfileEmailNotificationsHeader>
              {description}
            </ProfileEmailNotificationsHeader>
          </ProfileEmailNotificationsOptionGrid>
        )}
        <Select
          variant="standard"
          options={options}
          label=""
          selected={selectedFrequency}
          height="Dense"
          inputProps={inputProps}
          onChange={(value: string) => updateFrequency(field, value)}
        />
        <ProfileEmailNotificationExplainer none={selectedFrequency === "None"}>
          {getExplainerMessage(selectedFrequency, explainerType)}
        </ProfileEmailNotificationExplainer>
      </ProfileEmailNotificationsGridRows>
    );
  };

  const renderSectionHeaderRow = (title: string, description: string) => (
    <ProfileEmailNotificationsGridRows>
      <ProfileEmailNotificationsOptionGrid>
        <ProfileEmailNotificationsOption>
          {title}
        </ProfileEmailNotificationsOption>
        <ProfileEmailNotificationsHeader>
          {description}
        </ProfileEmailNotificationsHeader>
      </ProfileEmailNotificationsOptionGrid>
    </ProfileEmailNotificationsGridRows>
  );

  const updatedNotificationFrequency = async () => {
    if (auth?.token) {
      setSubmitting(true);
      const postNotificationResponse = await PostEmailNotifications(
        auth.token,
        updatedFrequency,
        setLoggedOut,
      );
      if (postNotificationResponse?.ok) {
        const newNotifications = await postNotificationResponse.json();
        setNotificationFrequency(newNotifications);
      }
      setNotificationUpdated("updated");
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (notificationUpdated) {
      timeout = setTimeout(() => setNotificationUpdated(""), 3000);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [notificationUpdated]);

  return (
    <GeneralInfoWrapper>
      <GeneralInfoText>EMAIL NOTIFICATIONS</GeneralInfoText>
      {notificationUpdated && (
        <ErrorWrapper>
          <ErrorPopup
            message="Notifications updated successfully!"
            hideMessage={setNotificationUpdated}
          />
        </ErrorWrapper>
      )}
      <ProfileEmailNotificationsGrid>
        <DisableSection disable={!updatedFrequency.is_subscribed}>
          {renderFrequencyRow({
            field: "report_frequency",
            title: "New reports",
            description: "Reports that contain the results of tests",
            options: REPORT_NOTIFICATION_OPTIONS,
            explainerType: "report",
          })}
          {providerCfcEnabled &&
            renderFrequencyRow({
              field: "cfc_eligibility_frequency",
              title: "Unity Confirm eligibility",
              description:
                "When a patient becomes eligible to order Unity Confirm",
              options: IMMEDIATE_DAILY_NONE_OPTIONS,
              explainerType: "report",
              inputProps: {
                "aria-label": "Unity Confirm eligibility frequency",
              },
            })}
          {renderFrequencyRow({
            field: "missing_clinical_info_frequency",
            title: "Missing clinical information",
            description:
              "Examples: Patient’s name, due date and physician signature",
            options: MISSING_CLINICAL_INFO_OPTIONS,
            explainerType: "clinical",
          })}
          <ProfileEmailNotificationsGridLine />
          {renderSectionHeaderRow(
            "Missing billing information",
            "Insurance or billing info is incomplete or needs updates",
          )}
          {renderFrequencyRow({
            field: "missing_billing_frequency_new",
            title: "New open cases",
            options: IMMEDIATE_DAILY_NONE_OPTIONS,
            explainerType: "billing",
            indent: true,
          })}
          {renderFrequencyRow({
            field: "missing_billing_frequency_existing",
            title: "All open cases",
            options: EXISTING_OPEN_ITEM_OPTIONS,
            explainerType: "billing",
            indent: true,
          })}
          <ProfileEmailNotificationsGridLine />
          {renderSectionHeaderRow(
            "Treatment notes",
            "Clinical visit notes with referral details (requested by some insurance plans for claim approval)",
          )}
          {renderFrequencyRow({
            field: "missing_treatment_notes_frequency_new",
            title: "New open requests",
            options: IMMEDIATE_DAILY_NONE_OPTIONS,
            explainerType: "treatment",
            indent: true,
          })}
          {renderFrequencyRow({
            field: "missing_treatment_notes_frequency_existing",
            title: "All open requests",
            options: EXISTING_OPEN_ITEM_OPTIONS,
            explainerType: "treatment",
            indent: true,
          })}
        </DisableSection>
        <ProfileEmailNotificationsGridLine />
        <ProfileEmailNotificationsGridRows>
          <ProfileEmailNotificationsOptionGrid>
            <ProfileEmailNotificationsOption>
              Notification preference
            </ProfileEmailNotificationsOption>
            <ProfileEmailNotificationsHeader />
          </ProfileEmailNotificationsOptionGrid>
          <Select
            variant="standard"
            options={SUBSCRIPTION_OPTIONS}
            label=""
            selected={updatedFrequency.is_subscribed ? "True" : "False"}
            height="Dense"
            onChange={(val: string) => {
              setUpdateFrequency((previousFrequency) => ({
                ...previousFrequency,
                is_subscribed: val === "True",
              }));
            }}
          />
          {updatedFrequency.is_subscribed === false && (
            <ProfileEmailNotificationExplainer
              none={updatedFrequency.is_subscribed === false}
            >
              If you unsubscribe from all emails, we will have no way of
              notifying you of account-related or order-related information.
            </ProfileEmailNotificationExplainer>
          )}
        </ProfileEmailNotificationsGridRows>
      </ProfileEmailNotificationsGrid>
      <ProfileEmailNotificationsButtonWrapper>
        {submitting ? (
          <ProfileEmailNotificationsSpinner>
            {" "}
            <img src={loading} alt="Loading" />
          </ProfileEmailNotificationsSpinner>
        ) : (
          <Button
            label="SAVE"
            className="save-button-notification"
            onClick={() => {
              updatedNotificationFrequency();
            }}
            size="medium"
            disabled={!hasNotificationChanges}
          />
        )}
        <Button
          label="CANCEL"
          onClick={() => {
            setUpdateFrequency(notificationFrequency);
          }}
          size="medium"
          reverse
          disabled={!hasNotificationChanges}
        />
      </ProfileEmailNotificationsButtonWrapper>
    </GeneralInfoWrapper>
  );
};

export default EmailNotifications;
